import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import useCreate from "../../hooks/useCreate";
import useDelete from "../../hooks/useDelete";
import useGet from "../../hooks/useGet";
import useUpdate from "../../hooks/useUpdate";
import API_BASE_URL from "../../config/api";
import { useAuth } from "pages/AuthProvider";

const normalizeCollection = (data, keys = []) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;

  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
  }

  return [];
};

const createEmptyCondition = () => ({
  operator: "lte",
  value: ""
});

const OPERATOR_OPTIONS = [
  { value: "eq", label: "Equals" },
  { value: "lte", label: "Less than or equal to" },
  { value: "gte", label: "Greater than or equal to" },
  { value: "lt", label: "Less than" },
  { value: "gt", label: "Greater than" },
  { value: "in", label: "In list (comma separated)" }
];

const resolveImageUrl = (item) => {
  const raw =
    item?.image?.url ||
    item?.image?.path ||
    item?.imageUrl ||
    item?.thumbnail ||
    item?.photo ||
    item?.image;
  if (!raw || typeof raw !== "string") return "";
  if (/^(https?:|blob:|data:)/i.test(raw)) return raw;
  const normalized = raw.replace(/^\/+/, "");
  if (normalized.startsWith("uploads/")) return `${API_BASE_URL}/${normalized}`;
  if (raw.startsWith("/")) return `${API_BASE_URL}${raw}`;
  return `${API_BASE_URL}/uploads/${normalized}`;
};

const resolveBannerUrl = (item) => {
  const raw =
    item?.bannerImageDesktop?.url ||
    item?.bannerImageDesktop?.path ||
    item?.bannerImageDesktopUrl ||
    item?.bannerImageDesktop;
  if (!raw || typeof raw !== "string") return "";
  if (/^(https?:|blob:|data:)/i.test(raw)) return raw;
  const normalized = raw.replace(/^\/+/, "");
  if (normalized.startsWith("uploads/")) return `${API_BASE_URL}/${normalized}`;
  if (raw.startsWith("/")) return `${API_BASE_URL}${raw}`;
  return `${API_BASE_URL}/uploads/${normalized}`;
};

const resolveBannerMobileUrl = (item) => {
  const raw =
    item?.bannerImageMobile?.url ||
    item?.bannerImageMobile?.path ||
    item?.bannerImageMobileUrl ||
    item?.bannerImageMobile;
  if (!raw || typeof raw !== "string") return "";
  if (/^(https?:|blob:|data:)/i.test(raw)) return raw;
  const normalized = raw.replace(/^\/+/, "");
  if (normalized.startsWith("uploads/")) return `${API_BASE_URL}/${normalized}`;
  if (raw.startsWith("/")) return `${API_BASE_URL}${raw}`;
  return `${API_BASE_URL}/uploads/${normalized}`;
};

const formatConditionValue = (value) => {
  if (Array.isArray(value)) return value.join(", ");
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

const formatConditions = (conditions) => {
  if (!Array.isArray(conditions) || conditions.length === 0) return "-";

  return conditions
    .map((condition) => {
      const field = condition?.field || "field";
      const operator = condition?.operator || "eq";
      return `${field} ${operator} ${formatConditionValue(condition?.value)}`;
    })
    .join(" | ");
};

const parseConditionValue = (operator, value) => {
  if (operator === "in") {
    return String(value)
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (["lte", "gte", "lt", "gt"].includes(operator)) {
    const num = Number(value);
    return Number.isNaN(num) ? value : num;
  }

  return value;
};

function CategoryTags() {
  const navigate = useNavigate();
  const { canAccess } = useAuth();
  const { categoryId } = useParams();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingTagId, setEditingTagId] = useState(null);
  const [viewTag, setViewTag] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [bannerDesktopFile, setBannerDesktopFile] = useState(null);
  const [bannerDesktopPreview, setBannerDesktopPreview] = useState("");
  const [bannerMobileFile, setBannerMobileFile] = useState(null);
  const [bannerMobilePreview, setBannerMobilePreview] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    type: "manual",
    currency: "INR",
    isActive: true,
    conditions: [createEmptyCondition()]
  });

  const { data: categoriesData } = useGet("/api/categories?all=true");
  const tagsEndpoint = categoryId ? `/api/categories/tags/${categoryId}` : null;
  const { data: tagsData, loading, mutate } = useGet(tagsEndpoint);
  const { create } = useCreate("/api/tag");
  const { remove } = useDelete("/api/tag");
  const { update } = useUpdate("/api/tag");

  const categories = useMemo(
    () => normalizeCollection(categoriesData, ["categories", "data", "items"]),
    [categoriesData]
  );
  const selectedCategory = useMemo(
    () => categories.find((category) => String(category?._id || category?.id) === String(categoryId)),
    [categories, categoryId]
  );
  const tags = useMemo(
    () => normalizeCollection(tagsData, ["tags", "data", "items"]),
    [tagsData]
  );

  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
      if (bannerDesktopPreview && bannerDesktopPreview.startsWith("blob:")) {
        URL.revokeObjectURL(bannerDesktopPreview);
      }
      if (bannerMobilePreview && bannerMobilePreview.startsWith("blob:")) {
        URL.revokeObjectURL(bannerMobilePreview);
      }
    };
  }, [imagePreview, bannerDesktopPreview, bannerMobilePreview]);

  const resetForm = () => {
    setFormData({
      name: "",
      type: "manual",
      currency: "INR",
      isActive: true,
      conditions: [createEmptyCondition()]
    });
    if (imagePreview && imagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview("");
    setImageFile(null);
    if (bannerDesktopPreview && bannerDesktopPreview.startsWith("blob:")) {
      URL.revokeObjectURL(bannerDesktopPreview);
    }
    setBannerDesktopPreview("");
    setBannerDesktopFile(null);
    if (bannerMobilePreview && bannerMobilePreview.startsWith("blob:")) {
      URL.revokeObjectURL(bannerMobilePreview);
    }
    setBannerMobilePreview("");
    setBannerMobileFile(null);
    setEditingTagId(null);
    setErrorMessage("");
  };

  const handleOpen = () => {
    resetForm();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const handleEdit = (tag) => {
    const tagConditions =
      Array.isArray(tag.conditions) && tag.conditions.length > 0
        ? tag.conditions.map((condition) => ({
            operator: condition.operator || "lte",
            value: Array.isArray(condition.value) ? condition.value.join(", ") : String(condition.value ?? "")
          }))
        : [createEmptyCondition()];

    setEditingTagId(tag._id || tag.id || null);
    setFormData({
      name: tag.name || "",
      type: tag.type || "manual",
      currency: tag.currency || "INR",
      isActive: tag.isActive !== false,
      conditions: tagConditions
    });
    setImageFile(null);
    setImagePreview(resolveImageUrl(tag));
    setBannerDesktopFile(null);
    setBannerDesktopPreview(resolveBannerUrl(tag));
    setBannerMobileFile(null);
    setBannerMobilePreview(resolveBannerMobileUrl(tag));
    setErrorMessage("");
    setOpen(true);
  };

  const handleSubmit = async () => {
    if (!categoryId || !formData.name.trim()) {
      setErrorMessage("Category and tag name are required.");
      return;
    }

    const cleanedConditions = formData.conditions
      .filter((condition) => condition.operator)
      .map((condition) => ({
        field: "price",
        operator: condition.operator,
        value: parseConditionValue(condition.operator, condition.value)
      }));

    if (formData.type === "dynamic" && cleanedConditions.length === 0) {
      setErrorMessage("At least one condition is required for dynamic tags.");
      return;
    }

    setSubmitting(true);
    setErrorMessage("");

    try {
      const payload = {
        name: formData.name.trim(),
        category: categoryId,
        type: formData.type,
        currency: formData.currency,
        isActive: Boolean(formData.isActive),
        conditions: formData.type === "dynamic" ? cleanedConditions : []
      };
      const hasFile = Boolean(imageFile || bannerDesktopFile || bannerMobileFile);
      const finalPayload = hasFile
        ? (() => {
            const data = new FormData();
            data.append("name", payload.name);
            data.append("category", payload.category);
            data.append("type", payload.type);
            data.append("currency", payload.currency);
            data.append("isActive", String(payload.isActive));
            data.append("conditions", JSON.stringify(payload.conditions || []));
            if (imageFile) data.append("image", imageFile);
            if (bannerDesktopFile) data.append("bannerImageDesktop", bannerDesktopFile);
            if (bannerMobileFile) data.append("bannerImageMobile", bannerMobileFile);
            return data;
          })()
        : payload;

      if (editingTagId) {
        await update(editingTagId, finalPayload);
      } else {
        await create(finalPayload);
      }

      await mutate();
      handleClose();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || "Failed to save tag.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (tag) => {
    const id = tag?._id || tag?.id;
    if (!id) return;

    const confirmed = window.confirm(`Delete tag "${tag?.name || "this tag"}"?`);
    if (!confirmed) return;

    try {
      await remove(id, { revalidate: false });
      if (String(viewTag?._id || viewTag?.id || "") === String(id)) {
        setViewTag(null);
      }
      await mutate();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || "Failed to delete tag.");
    }
  };

  const updateCondition = (index, key, value) => {
    setFormData((prev) => ({
      ...prev,
      conditions: prev.conditions.map((condition, conditionIndex) =>
        conditionIndex === index ? { ...condition, [key]: value } : condition
      )
    }));
  };

  const addCondition = () => {
    setFormData((prev) => ({
      ...prev,
      conditions: [...prev.conditions, createEmptyCondition()]
    }));
  };

  const removeCondition = (index) => {
    setFormData((prev) => ({
      ...prev,
      conditions: prev.conditions.filter((_, conditionIndex) => conditionIndex !== index)
    }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (imagePreview && imagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreview);
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleBannerDesktopChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (bannerDesktopPreview && bannerDesktopPreview.startsWith("blob:")) {
      URL.revokeObjectURL(bannerDesktopPreview);
    }
    setBannerDesktopFile(file);
    setBannerDesktopPreview(URL.createObjectURL(file));
  };

  const handleBannerMobileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (bannerMobilePreview && bannerMobilePreview.startsWith("blob:")) {
      URL.revokeObjectURL(bannerMobilePreview);
    }
    setBannerMobileFile(file);
    setBannerMobilePreview(URL.createObjectURL(file));
  };

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          border: "1px solid #e2e8f0",
          borderRadius: 3,
          background: "linear-gradient(135deg, #eff6ff 0%, #ecfeff 100%)"
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Category Tags
            </Typography>
            <Typography color="text.secondary">Category: {selectedCategory?.name || "Unknown"}</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" onClick={() => navigate("/AddCategory")}>
              Back to Categories
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpen}>
              Add Tag
            </Button>
          </Box>
        </Box>
      </Paper>

      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                  <TableCell>#</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Conditions</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6}>Loading tags...</TableCell>
                  </TableRow>
                ) : tags.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>No tags found for this category.</TableCell>
                  </TableRow>
                ) : (
                  tags.map((tag, index) => (
                    <TableRow key={tag._id || tag.id || `${tag.name}-${index}`} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <SellOutlinedIcon fontSize="small" color="action" />
                          <Typography variant="body2">{tag.name || "-"}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={tag.type || "-"} variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={tag.isActive ? "Active" : "Inactive"}
                          color={tag.isActive ? "success" : "default"}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 420, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {formatConditions(tag.conditions)}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View">
                          <IconButton size="small" color="info" onClick={() => setViewTag(tag)}>
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canAccess('categories', 'edit') && (
                          <Tooltip title="Edit">
                            <IconButton size="small" color="primary" onClick={() => handleEdit(tag)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canAccess('categories', 'delete') && (
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDelete(tag)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{editingTagId ? "Edit Tag" : "Add Tag"}</DialogTitle>
        <DialogContent sx={{ pt: 1, display: "grid", gap: 2 }}>
          <TextField
            label="Category"
            value={selectedCategory?.name || ""}
            fullWidth
            disabled
          />

          <TextField label="Tag Name" value={formData.name} onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))} fullWidth />

          <Box sx={{ display: "grid", gap: 1 }}>
            <Button variant="outlined" component="label">
              Upload Image
              <input type="file" hidden accept="image/*" onChange={handleImageChange} />
            </Button>
            {imagePreview ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <img
                  src={imagePreview}
                  alt="tag preview"
                  style={{ width: 64, height: 64, borderRadius: 8, objectFit: "cover" }}
                />
                <Typography variant="caption" color="text.secondary">
                  {imageFile ? imageFile.name : "Current image"}
                </Typography>
              </Box>
            ) : null}
          </Box>

          <Box sx={{ display: "grid", gap: 1 }}>
            <Button variant="outlined" component="label">
              Upload Banner (Desktop)
              <input type="file" hidden accept="image/*" onChange={handleBannerDesktopChange} />
            </Button>
            {bannerDesktopPreview ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <img
                  src={bannerDesktopPreview}
                  alt="tag banner desktop preview"
                  style={{ width: 120, height: 56, borderRadius: 8, objectFit: "cover" }}
                />
                <Typography variant="caption" color="text.secondary">
                  {bannerDesktopFile ? bannerDesktopFile.name : "Current desktop banner"}
                </Typography>
              </Box>
            ) : null}
          </Box>

          <Box sx={{ display: "grid", gap: 1 }}>
            <Button variant="outlined" component="label">
              Upload Banner (Mobile)
              <input type="file" hidden accept="image/*" onChange={handleBannerMobileChange} />
            </Button>
            {bannerMobilePreview ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <img
                  src={bannerMobilePreview}
                  alt="tag banner mobile preview"
                  style={{ width: 96, height: 56, borderRadius: 8, objectFit: "cover" }}
                />
                <Typography variant="caption" color="text.secondary">
                  {bannerMobileFile ? bannerMobileFile.name : "Current mobile banner"}
                </Typography>
              </Box>
            ) : null}
          </Box>

          <FormControl fullWidth>
            <InputLabel id="category-tag-type-label">Type</InputLabel>
            <Select
              labelId="category-tag-type-label"
              label="Type"
              value={formData.type}
              onChange={(event) => setFormData((prev) => ({ ...prev, type: event.target.value }))}
            >
              <MenuItem value="manual">manual</MenuItem>
              <MenuItem value="dynamic">dynamic</MenuItem>
              <MenuItem value="system">system</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="category-tag-currency-label">Currency</InputLabel>
            <Select
              labelId="category-tag-currency-label"
              label="Currency"
              value={formData.currency}
              onChange={(event) => setFormData((prev) => ({ ...prev, currency: event.target.value }))}
            >
              <MenuItem value="USD">US (USD)</MenuItem>
              <MenuItem value="INR">IN (INR)</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="category-tag-status-label">Status</InputLabel>
            <Select
              labelId="category-tag-status-label"
              label="Status"
              value={formData.isActive ? "active" : "inactive"}
              onChange={(event) => setFormData((prev) => ({ ...prev, isActive: event.target.value === "active" }))}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>

          {formData.type === "dynamic" && (
            <>
              <Divider />
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="subtitle2">Conditions</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={addCondition}>
                  Add Condition
                </Button>
              </Box>

              {formData.conditions.map((condition, index) => (
                <Box key={`condition-${index}`} sx={{ display: "grid", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Field: Price
                  </Typography>
                  <FormControl fullWidth>
                    <InputLabel id={`category-tag-condition-operator-${index}`}>Operator</InputLabel>
                    <Select
                      labelId={`category-tag-condition-operator-${index}`}
                      label="Operator"
                      value={condition.operator}
                      onChange={(event) => updateCondition(index, "operator", event.target.value)}
                    >
                      {OPERATOR_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <TextField
                      label={condition.operator === "in" ? "Value (comma separated)" : "Value"}
                      value={condition.value}
                      onChange={(event) => updateCondition(index, "value", event.target.value)}
                      fullWidth
                    />
                    <IconButton onClick={() => removeCondition(index)} disabled={formData.conditions.length === 1} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </>
          )}

          {errorMessage ? <Typography color="error">{errorMessage}</Typography> : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Saving..." : editingTagId ? "Update Tag" : "Save Tag"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(viewTag)} onClose={() => setViewTag(null)} fullWidth maxWidth="sm">
        <DialogTitle>Tag Details</DialogTitle>
        <DialogContent dividers>
          {viewTag ? (
            <Box sx={{ display: "grid", gap: 2 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  background: "linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)"
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <SellOutlinedIcon color="action" />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {viewTag.name || "-"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
                  <Chip size="small" variant="outlined" label={`Category: ${selectedCategory?.name || "-"}`} />
                  <Chip size="small" variant="outlined" label={`Type: ${viewTag.type || "-"}`} />
                  <Chip size="small" variant="outlined" label={`Currency: ${viewTag.currency || "-"}`} />
                  <Chip
                    size="small"
                    label={viewTag.isActive ? "Active" : "Inactive"}
                    color={viewTag.isActive ? "success" : "default"}
                  />
                </Box>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Media
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Image
                    </Typography>
                    <Box
                      sx={{
                        width: 96,
                        height: 96,
                        borderRadius: 2,
                        overflow: "hidden",
                        border: "1px solid #e2e8f0",
                        backgroundColor: "#fff"
                      }}
                    >
                      {resolveImageUrl(viewTag) ? (
                        <img
                          src={resolveImageUrl(viewTag)}
                          alt={`${viewTag.name || "Tag"} image`}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <Box sx={{ height: "100%", display: "grid", placeItems: "center" }}>
                          <Typography variant="caption" color="text.secondary">
                            No image
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Banner (Desktop)
                    </Typography>
                    <Box
                      sx={{
                        width: 180,
                        height: 96,
                        borderRadius: 2,
                        overflow: "hidden",
                        border: "1px solid #e2e8f0",
                        backgroundColor: "#fff"
                      }}
                    >
                      {resolveBannerUrl(viewTag) ? (
                        <img
                          src={resolveBannerUrl(viewTag)}
                          alt={`${viewTag.name || "Tag"} banner desktop`}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <Box sx={{ height: "100%", display: "grid", placeItems: "center" }}>
                          <Typography variant="caption" color="text.secondary">
                            No desktop banner
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Banner (Mobile)
                    </Typography>
                    <Box
                      sx={{
                        width: 140,
                        height: 96,
                        borderRadius: 2,
                        overflow: "hidden",
                        border: "1px solid #e2e8f0",
                        backgroundColor: "#fff"
                      }}
                    >
                      {resolveBannerMobileUrl(viewTag) ? (
                        <img
                          src={resolveBannerMobileUrl(viewTag)}
                          alt={`${viewTag.name || "Tag"} banner mobile`}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <Box sx={{ height: "100%", display: "grid", placeItems: "center" }}>
                          <Typography variant="caption" color="text.secondary">
                            No mobile banner
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Conditions
                </Typography>
                {Array.isArray(viewTag.conditions) && viewTag.conditions.length > 0 ? (
                  <Box sx={{ display: "grid", gap: 1 }}>
                    {viewTag.conditions.map((condition, index) => (
                      <Typography key={`view-condition-${index}`}>
                        {index + 1}. {condition?.field || "field"} {condition?.operator || "eq"} {formatConditionValue(condition?.value)}
                      </Typography>
                    ))}
                  </Box>
                ) : (
                  <Typography color="text.secondary">No conditions configured.</Typography>
                )}
              </Paper>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewTag(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CategoryTags;
